import {
  CompanyOrt,
  CompanyUpdateData,
  CreateCompanyOrt,
  OrtPeriod as OrtPeriodFetched,
} from '@types';
import { Info } from 'luxon';
import { getISODate } from 'shared/utils/date-utils';
import { CompanyFormData } from './company-form';
import { CompanyOrtFormData, OrtPeriod } from './company-ort-form';

export const HOLIDAY_OPTIONS = [
  'Nieuwjaarsdag',
  'Goede Vrijdag',
  'Eerste Paasdag',
  'Tweede Paasdag',
  'Koningsdag',
  'Bevrijdingsdag elk jaar',
  'Hemelvaartdag',
  'Vrijdag na Hemelvaartdag',
  'Vrijdag voorafgaand Pinksterweekend',
  'Eerste Pinksterdag',
  'Tweede Pinksterdag',
  'Kerstavond',
  'Eerste Kerstdag',
  'Tweede Kerstdag',
  'Oudejaarsavond',
];

export const DAYS_OF_WEEK = Info.weekdays('long', { locale: 'nl' });

export const DAY_ORDER = Info.weekdays('long', { locale: 'en' }).map((day) => day.toLowerCase());

export const DAY_NAME_MAP = Object.fromEntries(
  DAYS_OF_WEEK.map((dutch, i) => [dutch, DAY_ORDER[i]])
);
export const ENGLISH_TO_DUTCH = Object.fromEntries(
  DAY_ORDER.map((english, i) => [english, DAYS_OF_WEEK[i]])
);

export type OrtData = {
  [day: string]: OrtPeriod[];
};

export type HolidayData = {
  [holidayName: string]: OrtPeriod[];
};

export const companyDataPrepareForSubmit = (data: CompanyFormData): CompanyUpdateData => {
  const processedData = {
    ortId: data.ortId,
    ortStartDate: data.ortStartDate,
    workCooldownMinutes: data.workCooldownMinutes,
    offersFreelanceWork: data.offersFreelanceWork,
    offersTempWork: data.offersTempWork,
    offersEmployeeWork: data.offersEmployeeWork,
    maxFLTravelAllowanceCents: toCentsOrNull(data.maxFLTravelAllowance),
    maxParkingCents: toCentsOrNull(data.maxParking),
    maxTWTravelAllowanceCents: toCentsOrNull(data.maxTWTravelAllowance),
    maxMileageAllowanceMeters: toNumberOrNull(data.maxMileageAllowance),
    receiptCertificateIds: data.receiptCertificates?.map((certificate) => certificate.id) ?? [],
    themeCustomizationEnabled: data.themeCustomizationEnabled,
    allowSSOFeatures: data.allowSSOFeatures,
  };

  return processedData;
};

const toNumberOrNull = (value: string | number | undefined): number | null => {
  if (value === '' || value === undefined) {
    return null;
  }
  return Number(value);
};

const toCentsOrNull = (value: string | number | undefined): number | null => {
  const numericValue = toNumberOrNull(value);
  return numericValue !== null ? numericValue * 100 : null;
};

export const formatTimeToUi = (time: string) => {
  const formattedTime = time.slice(0, 5);
  return formattedTime === '24:00' ? '23:59' : formattedTime;
};

export const formatTimeToBackend = (time: string) => {
  const formattedTime = time === '23:59' ? '24:00' : time;
  return `${formattedTime}:00`;
};

export function formatOrtPeriods(periods: OrtPeriod[]): any[] {
  return periods.map((period) => ({
    startTime: formatTimeToBackend(period.startTime || '00:00'),
    endTime: formatTimeToBackend(period.endTime || '24:00'),
    surcharge: !period.surcharge ? null : Number(period.surcharge),
    surchargeType: period.surchargeType,
  }));
}

const getDayData = (ort: CompanyOrt | undefined, dayName: string) => {
  if (!ort?.calendar || typeof ort.calendar !== 'object') {
    return undefined;
  }
  return ort.calendar.workDays?.[dayName as keyof typeof ort.calendar.workDays];
};

function getInitialOrtDataForDays(
  ort: CompanyOrt | undefined,
  days: string[],
  dayNameMap: { [key: string]: string }
): OrtData {
  const initialData: OrtData = {};

  days.forEach((day) => {
    const apiDayName = dayNameMap[day];
    const dayData = getDayData(ort, apiDayName);

    if (Array.isArray(dayData) && dayData.length > 0) {
      initialData[day] = dayData.map((period: OrtPeriodFetched) => ({
        startTime: formatTimeToUi(period.startTime),
        endTime: formatTimeToUi(period.endTime),
        surcharge:
          period.surcharge !== undefined && period.surcharge !== null ? period.surcharge : 0,
        surchargeType: period.surchargeType,
      }));
    }
  });
  return initialData;
}

function getInitialOrtDataForHolidays(holidaysData: Record<string, any> | undefined): HolidayData {
  const initialHolidays: HolidayData = {};

  if (holidaysData && Object.keys(holidaysData).length > 0) {
    Object.entries(holidaysData).forEach(([name, periods]) => {
      if (Array.isArray(periods) && periods.length > 0) {
        initialHolidays[name] = periods.map((period: OrtPeriodFetched) => ({
          startTime: formatTimeToUi(period.startTime),
          endTime: formatTimeToUi(period.endTime),
          surcharge:
            period.surcharge !== undefined && period.surcharge !== null ? period.surcharge : 0,
          surchargeType: period.surchargeType,
        }));
      }
    });
  }

  return initialHolidays;
}

export function sortPeriodsByTime<T extends { startTime: string; endTime: string }>(
  periodsData: Record<string, T[]>
): Record<string, T[]> {
  const sorted: Record<string, T[]> = {};

  Object.entries(periodsData).forEach(([key, periods]) => {
    sorted[key] = [...periods].sort((a, b) => {
      const aStartMinutes = timeToMinutes(a.startTime);
      const bStartMinutes = timeToMinutes(b.startTime);

      if (aStartMinutes !== bStartMinutes) {
        return aStartMinutes - bStartMinutes;
      }

      const aEndMinutes = timeToMinutes(a.endTime);
      const bEndMinutes = timeToMinutes(b.endTime);
      return aEndMinutes - bEndMinutes;
    });
  });

  return sorted;
}

export const timeToMinutes = (time: string): number => {
  const parts = time.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  return hours * 60 + minutes;
};

export const formatTime = (time: string): string => {
  if (time.split(':').length === 2) {
    return `${time}:00`;
  }
  return time;
};

export const ortDayData = (orts: CompanyOrt | undefined) => {
  const initialOrtData = getInitialOrtDataForDays(orts, DAYS_OF_WEEK, DAY_NAME_MAP);
  const days: { [key: string]: OrtPeriod[] } = {};
  DAYS_OF_WEEK.forEach((day) => {
    const periods = initialOrtData[day] || [];
    days[day] = periods;
  });

  return days;
};

export const holidayData = (orts: CompanyOrt) => {
  const initialHolidaysData = getInitialOrtDataForHolidays(orts?.holidays);
  if (Object.keys(initialHolidaysData).length === 0) {
    return {};
  }
  return initialHolidaysData;
};

export const transformFormDataToOrt = (
  formData: CompanyOrtFormData,
  companyId: string
): CreateCompanyOrt => {
  const workDays: Record<string, OrtPeriod[]> = {};

  Object.entries(formData.days).forEach(([day, periods]) => {
    const englishDay = DAY_NAME_MAP[day] || day.toLowerCase();
    workDays[englishDay] = getValidPeriods(periods);
  });
  const sortedWorkDays = sortPeriodsByTime(workDays);

  const holidays: Record<string, OrtPeriod[]> = {};
  if (formData.holidays) {
    Object.entries(formData.holidays).forEach(([holidayName, periods]) => {
      if (!holidayName || holidayName.trim() === '') {
        return;
      }
      holidays[holidayName] = getValidPeriods(periods);
    });
  }

  const sortedHolidays = sortPeriodsByTime(holidays);
  const utcStartDate = getISODate(formData.utcStartDate);

  return {
    companyId,
    name: formData.name ?? null,
    employmentType: formData.employmentType,
    utcStartDate,
    createdAt: new Date().toISOString(),
    calendar: { workDays: sortedWorkDays },
    holidays: sortedHolidays,
    ...(formData.ortId ? { id: formData.ortId } : {}),
  };
};

export const checkPeriodsOverlap = (
  start1: string,
  end1: string,
  start2: string,
  end2: string
): boolean => {
  const start1Minutes = timeToMinutes(start1);
  const end1Minutes = timeToMinutes(end1);
  const start2Minutes = timeToMinutes(start2);
  const end2Minutes = timeToMinutes(end2);

  return start1Minutes < end2Minutes && end1Minutes > start2Minutes;
};

function getValidPeriods(periods: OrtPeriod[]): OrtPeriod[] {
  return periods
    .filter((period) => {
      const s = period.surcharge;
      return s !== undefined && s !== null && !isNaN(Number(s));
    })
    .map((period) => ({
      startTime: formatTime(period.startTime),
      endTime: formatTime(period.endTime),
      surcharge: period.surcharge,
      surchargeType: period.surchargeType,
    }));
}

export const findDefinedOrt = (ort: CompanyOrt): 'factor' | 'offset' | undefined => {
  const firstPeriod = Object.values(ort.calendar?.workDays ?? [])
    .flatMap((period) => period ?? []) // flatten the arrays
    .find((periodItem) => periodItem?.surchargeType); // find first valid item
  return firstPeriod?.surchargeType;
};
